import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';

/**
 * Counts the Ask surfaces (CopilotAskComponent) currently on screen
 * (.project#1877). The global Ask launcher reads `inlineAskMounted$` to hide
 * itself while Ask is already in front of the user — the /copilot Chat tab, or
 * the Ask panel itself — like the prototype does on its Chat page.
 *
 * Hide-only by design: the launcher ANDs `!inlineAskMounted` into its own
 * visibility rule, so this can never show the button to someone the rule hides.
 *
 * Lives in its own tiny file (no Copilot imports) because the eagerly loaded
 * layout injects it; the Ask component itself stays in the lazy chunk.
 */
@Injectable({ providedIn: 'root' })
export class CopilotAskSurfaceRegistry {
  private readonly mounted$ = new BehaviorSubject<number>(0);

  /** True while at least one Ask surface is mounted. */
  public readonly inlineAskMounted$: Observable<boolean> = this.mounted$.pipe(
    map((count) => count > 0),
    distinctUntilChanged(),
  );

  public mount(): void {
    this.mounted$.next(this.mounted$.value + 1);
  }

  /** Never goes below zero, so an unbalanced call cannot hide the launcher for good. */
  public unmount(): void {
    this.mounted$.next(Math.max(0, this.mounted$.value - 1));
  }
}
